import { useEffect, useState, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Star, ShieldCheck, ChevronDown, ChevronLeft, ChevronRight, ZoomIn, X } from "lucide-react";
import BroadcastModal from "../components/BroadcastModal";

const toAbs = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function ProductCardImage({ product, onMagnify }) {
  const gallery = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const u of [product.image_url, ...(product.images || [])]) {
      const url = (u || "").trim();
      if (url && !seen.has(url)) { seen.add(url); list.push(url); }
    }
    return list;
  }, [product]);
  const [idx, setIdx] = useState(0);
  const total = gallery.length;
  const clamp = (i) => (total === 0 ? 0 : (i + total) % total);
  const go = (e, delta) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => clamp(i + delta));
  };
  const current = gallery[idx] || product.image_url;
  const openMagnify = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onMagnify && current) onMagnify({ url: current, name: product.name, gallery, index: idx });
  };
  return (
    <div className="aspect-[4/5] bg-[#F0EFEA] mb-4 relative overflow-hidden">
      <img
        src={toAbs(current)}
        alt={product.name}
        onClick={openMagnify}
        className="w-full h-full object-contain transition-opacity duration-300 cursor-zoom-in bg-[#F5F4EF]"
        data-testid={`card-image-${product.our_code}`}
      />
      <button
        type="button"
        onClick={openMagnify}
        className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-[#1E2C5A] text-white border border-white/30 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="Magnify image"
        data-testid={`card-magnify-${product.our_code}`}
      >
        <ZoomIn className="w-4 h-4" strokeWidth={1.5} />
      </button>
      {product.ultra_premium && (
        <div className="absolute top-3 left-3 bg-[#1E2C5A] text-[#C9A968] px-3 py-1 text-[10px] uppercase tracking-[0.2em] border border-[#C9A968]/40 flex items-center gap-1" data-testid={`assured-${product.our_code}`}>
          <ShieldCheck className="w-3 h-3" strokeWidth={1.5} /> Assured
        </div>
      )}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => go(e, -1)}
            className="absolute top-1/2 left-2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-[#1E2C5A] text-white border border-white/30 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Previous image"
            data-testid={`card-prev-${product.our_code}`}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={(e) => go(e, 1)}
            className="absolute top-1/2 right-2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-black/50 hover:bg-[#1E2C5A] text-white border border-white/30 opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Next image"
            data-testid={`card-next-${product.our_code}`}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
          <div
            className="absolute bottom-3 right-3 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-white/30"
            data-testid={`card-image-counter-${product.our_code}`}
          >
            {idx + 1} / {total}
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5" data-testid={`card-dots-${product.our_code}`}>
            {gallery.map((url, i) => (
              <button
                key={url || `dot-${i}`}
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIdx(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-4" : "bg-white/50 hover:bg-white/80"}`}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
import { useApp } from "../context/AppContext";

const CATALOG_BACKDROPS = [
  "https://images.unsplash.com/photo-1615873968403-89e068629265?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1618221118493-9cfa1a1c00da?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1618220179428-22790b461013?auto=format&fit=crop&w=2400&q=80",
  "https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?auto=format&fit=crop&w=2400&q=80",
];

const PRICE_BUCKETS = [
  { key: "0-100", label: "Under ₹100", min: 0, max: 100 },
  { key: "100-500", label: "₹100 – ₹500", min: 100, max: 500 },
  { key: "500-1000", label: "₹500 – ₹1,000", min: 500, max: 1000 },
  { key: "1000-3000", label: "₹1,000 – ₹3,000", min: 1000, max: 3000 },
  { key: "3000+", label: "Above ₹3,000", min: 3000, max: Infinity },
];

// Reusable multi-select dropdown pill
function FilterPill({ label, values, selected, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const toggle = (v) => {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  };
  const count = selected.length;
  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
          count > 0 ? "bg-[#1E2C5A] text-white border-[#1E2C5A]" : "border-[#1A1A1A]/30 text-[#5C5C5C] hover:border-[#1A1A1A]"
        }`}
        data-testid={`${testId}-toggle`}
      >
        {label}{count > 0 && <span className="text-[#C9A968]">· {count}</span>}
        <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 bg-[#FAF9F6] border border-[#1A1A1A]/15 min-w-[220px] max-h-72 overflow-y-auto shadow-lg" data-testid={`${testId}-menu`}>
          {values.length === 0 ? (
            <p className="px-4 py-3 text-xs text-[#8A8A8A]">No values yet.</p>
          ) : values.map(v => (
            <label key={v.key || v} className="flex items-center gap-2 px-4 py-2 hover:bg-[#F0EFEA] cursor-pointer text-sm" data-testid={`${testId}-opt-${v.key || v}`}>
              <input
                type="checkbox"
                className="w-3.5 h-3.5 accent-[#1E2C5A]"
                checked={selected.includes(v.key || v)}
                onChange={() => toggle(v.key || v)}
              />
              <span className="capitalize">{v.label || v}</span>
            </label>
          ))}
          {count > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left px-4 py-2 text-xs uppercase tracking-wider text-red-700 border-t border-[#1A1A1A]/10 hover:bg-[#F0EFEA]"
              data-testid={`${testId}-clear`}
            >Clear</button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Catalog() {
  const { customer } = useApp();
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const [q, setQ] = useState("");
  const [bgIdx, setBgIdx] = useState(0);
  const [broadcasts, setBroadcasts] = useState([]);

  // Filter state
  const [selSizes, setSelSizes] = useState([]);
  const [selPrices, setSelPrices] = useState([]);
  const [selSurfaces, setSelSurfaces] = useState([]);
  const [selColours, setSelColours] = useState([]);
  const [selApps, setSelApps] = useState([]);
  const [magnify, setMagnify] = useState(null);  // { url, name, gallery, index } | null

  useEffect(() => {
    api.get("/categories").then(r => setCats(["all", ...r.data.categories]));
  }, []);

  useEffect(() => {
    // Fetch active buyer broadcasts once when the buyer arrives on Catalog
    api.get("/broadcasts/active?audience=buyers")
      .then(r => setBroadcasts(r.data || []))
      .catch(() => {});
  }, []);
  useEffect(() => {
    const p = {};
    if (cat !== "all") p.category = cat;
    if (q) p.q = q;
    api.get("/products", { params: p }).then(r => setProducts(r.data));
  }, [cat, q]);

  useEffect(() => {
    const t = setInterval(() => setBgIdx(i => (i + 1) % CATALOG_BACKDROPS.length), 6000);
    return () => clearInterval(t);
  }, []);

  const basePool = useMemo(() => products.filter(p => p.category !== "Tools"), [products]);

  // Case-insensitive dedupe + ascending alphabetical sort. Keeps the first-seen casing
  // (e.g. "Beige") so the buyer sees admin-typed values, not lowercased ones.
  const uniqSorted = (values) => {
    const seen = new Map(); // key: lowercased trim, val: first-seen casing
    for (const raw of values) {
      const v = (raw || "").trim();
      if (!v) continue;
      const k = v.toLowerCase();
      if (!seen.has(k)) seen.set(k, v);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  };

  const distinctSizes = useMemo(() => uniqSorted(basePool.map(p => p.size)), [basePool]);
  const distinctSurfaces = useMemo(() => uniqSorted(basePool.map(p => p.surface)), [basePool]);
  const distinctColours = useMemo(() => uniqSorted(basePool.map(p => p.colour)), [basePool]);
  const distinctApps = useMemo(() => uniqSorted(basePool.map(p => p.application_area)), [basePool]);

  const filtered = useMemo(() => basePool.filter(p => {
    const ciIncludes = (list, val) => list.map(x => x.toLowerCase()).includes((val || "").trim().toLowerCase());
    if (selSizes.length && !ciIncludes(selSizes, p.size)) return false;
    if (selSurfaces.length && !ciIncludes(selSurfaces, p.surface)) return false;
    if (selColours.length && !ciIncludes(selColours, p.colour)) return false;
    if (selApps.length && !ciIncludes(selApps, p.application_area)) return false;
    if (selPrices.length) {
      const price = Number(p.offer_price) || 0;
      const inBucket = selPrices.some(k => {
        const b = PRICE_BUCKETS.find(x => x.key === k);
        return b && price >= b.min && price < b.max;
      });
      if (!inBucket) return false;
    }
    return true;
  }), [basePool, selSizes, selPrices, selSurfaces, selColours, selApps]);

  const clearAll = () => {
    setSelSizes([]); setSelPrices([]); setSelSurfaces([]); setSelColours([]); setSelApps([]);
  };
  const activeCount = selSizes.length + selPrices.length + selSurfaces.length + selColours.length + selApps.length;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="catalog-page">
      {customer && (
        <BroadcastModal broadcasts={broadcasts} storageKey={`tr_bc_buyer_${customer.id}`} />
      )}
      {/* Hero band with rotating backdrop */}
      <div className="relative h-56 md:h-72 mb-12 overflow-hidden" data-testid="catalog-hero">
        {CATALOG_BACKDROPS.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${i === bgIdx ? "opacity-100" : "opacity-0"}`}
            data-testid={`catalog-bg-${i}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative h-full flex items-end p-6 md:p-10 text-white">
          <div>
            <p className="label-eyebrow !text-white/70">The Collection</p>
            <h1 className="font-editorial text-4xl sm:text-6xl tracking-editorial mt-2">Curated tiles</h1>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <p className="text-sm text-[#5C5C5C] max-w-md">Every tile below is verified for finish, batch consistency and grade. Filter by any attribute or search by name.</p>
        <input
          className="input-editorial max-w-xs"
          placeholder="Search by name..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="catalog-search"
        />
      </div>

      {/* Category quick filter */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="catalog-filters">
        {cats.map(c => (
          <button
            key={c}
            onClick={() => setParams(c === "all" ? {} : { cat: c })}
            className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              (cat === c || (cat === "all" && c === "all"))
                ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                : "border-[#1A1A1A]/30 text-[#5C5C5C] hover:border-[#1A1A1A]"
            }`}
            data-testid={`filter-${c}`}
          >{c}</button>
        ))}
      </div>

      {/* Filter By row */}
      <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="catalog-attribute-filters">
        <span className="label-eyebrow mr-2">Filter By</span>
        <FilterPill
          label="Size"
          values={distinctSizes}
          selected={selSizes}
          onChange={setSelSizes}
          testId="filter-size"
        />
        <FilterPill
          label="Price"
          values={PRICE_BUCKETS}
          selected={selPrices}
          onChange={setSelPrices}
          testId="filter-price"
        />
        <FilterPill
          label="Surface"
          values={distinctSurfaces}
          selected={selSurfaces}
          onChange={setSelSurfaces}
          testId="filter-surface"
        />
        <FilterPill
          label="Colour"
          values={distinctColours}
          selected={selColours}
          onChange={setSelColours}
          testId="filter-colour"
        />
        <FilterPill
          label="Application Area"
          values={distinctApps}
          selected={selApps}
          onChange={setSelApps}
          testId="filter-application"
        />
        {activeCount > 0 && (
          <button
            onClick={clearAll}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.2em] text-red-700 hover:underline"
            data-testid="clear-all-filters"
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Clear all
          </button>
        )}
        <span className="ml-auto text-xs text-[#8A8A8A]" data-testid="result-count">{filtered.length} products</span>
      </div>

      {filtered.length === 0 && (
        <p className="text-center text-[#8A8A8A] py-20" data-testid="catalog-empty">
          {q ? "Searched item not found" : activeCount > 0 ? "No products match your filters." : "No products in this category yet."}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
        {filtered.map(p => (
          <Link key={p.id} to={`/product/${p.id}`} className="group fade-up" data-testid={`product-${p.our_code}`}>
            <ProductCardImage product={p} onMagnify={setMagnify} />
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="label-eyebrow">{p.category} · {p.size}</p>
                <h3 className="font-editorial text-2xl mt-1 tracking-editorial group-hover:text-[#1E2C5A] transition-colors">{p.name}</h3>
                <p className="text-xs text-[#8A8A8A] mt-1">Code: {p.our_code}</p>
                {(p.surface || p.colour) && (
                  <p className="text-[10px] text-[#8A8A8A] mt-1 tracking-wider uppercase">
                    {[p.surface, p.colour].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="font-medium text-[#1A1A1A]">
                  ₹{p.offer_price}<span className="text-[#8A8A8A] text-xs">/{(p.primary_uom || "BOX").toLowerCase()}</span>
                  {p.tertiary_uom && Number(p.tertiary_uom_value) > 0 ? (
                    <span className="text-[#8A8A8A] text-xs ml-1">
                      (₹{(Number(p.offer_price) / Number(p.tertiary_uom_value)).toFixed(2)}/{p.tertiary_uom.toLowerCase()})
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-[#8A8A8A] line-through">₹{p.mrp}</p>
                <div className="flex items-center gap-1 justify-end mt-1 text-xs text-[#5C5C5C]">
                  <Star className="w-3 h-3 fill-[#1E2C5A] text-[#1E2C5A]" strokeWidth={1.5} /> {p.rating}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {magnify && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setMagnify(null)}
          data-testid="catalog-magnify-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setMagnify(null); }}
            className="absolute top-6 right-6 text-white/80 hover:text-white p-2"
            data-testid="catalog-magnify-close"
            aria-label="Close"
          >
            <X className="w-6 h-6" strokeWidth={1.5} />
          </button>
          {magnify.gallery && magnify.gallery.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const g = magnify.gallery;
                  const next = (magnify.index - 1 + g.length) % g.length;
                  setMagnify({ ...magnify, url: g[next], index: next });
                }}
                className="absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/30"
                data-testid="catalog-magnify-prev"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const g = magnify.gallery;
                  const next = (magnify.index + 1) % g.length;
                  setMagnify({ ...magnify, url: g[next], index: next });
                }}
                className="absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white border border-white/30"
                data-testid="catalog-magnify-next"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-[0.2em]">
                {magnify.index + 1} / {magnify.gallery.length}
              </div>
            </>
          )}
          <img
            src={toAbs(magnify.url)}
            alt={magnify.name || "magnified view"}
            className="max-w-[95vw] max-h-[92vh] object-contain select-none"
            onClick={(e) => e.stopPropagation()}
            data-testid="catalog-magnify-image"
          />
        </div>
      )}

    </div>
  );
}
